import React, { FC } from 'react';

import styled from '@emotion/styled';
import { useMediaQuery } from '@react-hook/media-query';
import { Icon, IconProps, Icons } from 'src/shared/ds/components/Icon';
import { SkeletonPlaceholder } from 'src/shared/ds/components/SkeletonPlaceholder';
import {
    BodyTextXSmall,
    ComponentTextSmall,
    HeadingText10,
    HeadingText20,
    HeadingText50,
} from 'src/shared/ds/Typography';
import { dsColors, DsColors, mediaQuery } from 'src/shared/ds/variables';
import { pxToRem } from 'src/shared/helpers/pxToRem';

export interface ActionProps {
    onClick: () => void;
    title: string;
    startIcon?: IconProps['name'];
    startIconUrl?: string;
    startIconOpacityReduced?: boolean;
    variant?: 'default' | 'red';
    endIconName?: Icons;
    endIconColor?: DsColors;
    description?: string;
    /**
     * Start Icon Background Color: this can be either key of `color` object property or a hex string
     */
    startIconBackgroundColor?: LiteralUnion<DsColors>;
    containerClassName?: string;
    initials?: string;
}

const getStyleBasedOnVariant = (type: ActionProps['variant']) => {
    switch (type) {
        case 'red':
            return VariantRedContainer;
        default:
            return VariantDefaultContainer;
    }
};

export const ActionItem: FC<ActionProps> = ({
    onClick,
    title,
    startIcon,
    startIconUrl,
    startIconOpacityReduced = false,
    endIconName = 'chevron-right',
    endIconColor = 'neutral400',
    variant = 'default',
    description,
    startIconBackgroundColor,
    containerClassName,
    initials,
}) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const Container = getStyleBasedOnVariant(variant);

    const TextComponent = smallScreenDetected ? HeadingText10 : HeadingText20;
    const DescriptionTextComponent = smallScreenDetected ? BodyTextXSmall : ComponentTextSmall;

    const renderIcon = () => {
        if (startIcon) {
            return <Icon name={startIcon} size={20} />;
        }

        if (startIconUrl) {
            return <StartIconImage src={startIconUrl} />;
        }

        if (initials) {
            return <HeadingText50>{initials}</HeadingText50>;
        }

        return <></>;
    };

    return (
        <Container onClick={onClick} className={containerClassName}>
            {(startIcon || startIconUrl || initials) && (
                <StartIconContainer
                    backgroundColor={
                        startIconBackgroundColor?.[0] === '#'
                            ? startIconBackgroundColor
                            : dsColors[startIconBackgroundColor as DsColors] ?? 'transparent'
                    }
                    opacity={startIconOpacityReduced ? 0.5 : 1}
                >
                    {renderIcon()}
                </StartIconContainer>
            )}
            <TextContainer>
                <TextComponent fontColor="neutral200">{title}</TextComponent>
                {description && (
                    <DescriptionTextContainer>
                        <DescriptionTextComponent fontColor="neutral400">
                            {description}
                        </DescriptionTextComponent>
                    </DescriptionTextContainer>
                )}
            </TextContainer>
            <div>
                <Icon name={endIconName} color={endIconColor} size={18} />
            </div>
        </Container>
    );
};

export interface ActionItemSkeletonProps {
    hideIcon?: boolean;
    hideDescription?: boolean;
}

export const ActionItemSkeleton: React.FC<ActionItemSkeletonProps> = ({
    hideIcon,
    hideDescription,
}) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    return (
        <BaseContainer>
            {!hideIcon && (
                <SkeletonPlaceholder
                    width={smallScreenDetected ? 40 : 56}
                    height={smallScreenDetected ? 40 : 56}
                    borderRadius={40}
                    marginRight={12}
                />
            )}

            <TextContainer>
                <SkeletonPlaceholder
                    width={200}
                    height={smallScreenDetected ? 18 : 20}
                    marginBottom={hideIcon ? 5 : 0}
                />

                {!hideDescription && (
                    <DescriptionTextContainer>
                        <SkeletonPlaceholder width={260} height={smallScreenDetected ? 14 : 16} />
                    </DescriptionTextContainer>
                )}
            </TextContainer>
        </BaseContainer>
    );
};

const BaseContainer = styled.div({
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: pxToRem(22),
    paddingBottom: pxToRem(22),
    display: 'flex',
    cursor: 'pointer',
    [mediaQuery.maxWidth('md')]: {
        paddingTop: pxToRem(16),
        paddingBottom: pxToRem(16),
    },
});

const VariantRedContainer = styled(BaseContainer)({
    backgroundColor: dsColors.red900,
});

const VariantDefaultContainer = styled(BaseContainer)({
    backgroundColor: dsColors.white,
});

const StartIconContainer = styled.div<{ backgroundColor: string; opacity: number }>(props => ({
    width: pxToRem(56),
    height: pxToRem(56),
    borderRadius: '50%',
    marginRight: pxToRem(12),
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: props.backgroundColor,
    opacity: props.opacity,
    display: 'flex',
    [mediaQuery.maxWidth('md')]: {
        width: pxToRem(40),
        height: pxToRem(40),
    },
}));

const StartIconImage = styled.img({
    width: '110%',
    height: '110%',
});

const TextContainer = styled.div({
    flex: 1,
    justifyContent: 'left',
    marginRight: pxToRem(17),
    display: 'flex',
    flexDirection: 'column',
});

const DescriptionTextContainer = styled.div({
    marginTop: pxToRem(4),
});
